import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp } from "./Reveal";
import { useLang } from "./LanguageContext";

const imgs = [
  "/stage/manifesto-curd-lux.webp",
  "/stage/manifesto-stretch-lux.webp",
  "https://images.unsplash.com/photo-1580638149300-65f0b9e8fbff?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

const GEN = "https://static.prod-images.emergentagent.com/jobs/6918af59-ab46-445b-a012-e513807d76c0/images/";
const tableSrcs = [
  `${GEN}50a0dee36351ed7c7028984a02e0aea40cdadd536c63bc50880f8d204c79d74d.jpeg`,
  `${GEN}afb719d3d9ca68199c32121f16152086a6a96f37774ae0e848a668e7df69ef94.jpeg`,
  `${GEN}eba166be0c434666ef474f55eeeb398dd30dfc0f3f83616a2a744260b79f2468.jpeg`,
  `${GEN}66c3fcfbec29135a8d5ac22d7a6b0301cdfbd6a6007af6748a0300693020e50b.jpeg`,
  `${GEN}fb0daceede8eef9d220f9f589a4ca1709038c6cc3fbbf207909c91e44ed141db.jpeg`,
  `${GEN}32912df6410d9fa92fb14438461803046ca57c92d818ff47da1056c5a7bcee4a.jpeg`,
];

const copy = {
  en: {
    kicker: "The Craft · Pasta Filata",
    h2: <>An age-old craft, performed with <em className="italic">rare precision.</em></>,
    chapters: [
      { n: "01", title: "The Curd", body: "It begins with the finest fresh milk and time-honoured technique. The curd rests in warm whey until supple and elastic — the quiet moment before the craft begins." },
      { n: "02", title: "The Stretch", body: "By hand, the curd is drawn and folded again and again — pasta filata. At the table, guests watch dull strands turn glossy as a warm sphere of mozzarella takes shape before their eyes." },
      { n: "03", title: "The Table", body: "Torn while still warm, dressed with cold-pressed oil and sea salt, and presented with quiet ceremony. A first taste no kitchen can replicate — the essence of the craft, served." },
    ],
    tableKicker: "03 · The Table",
    tableH3: <>Presented warm, composed with <em className="italic">intent.</em></>,
    tableAlts: ["Burrata & heirloom tomatoes", "Caprese, stacked", "Burrata, torn open", "Mozzarella & prosciutto", "Stracciatella & herbs", "Warm, over grilled peppers"],
  },
  it: {
    kicker: "Il Mestiere · Pasta Filata",
    h2: <>Un mestiere antico, eseguito con <em className="italic">rara precisione.</em></>,
    chapters: [
      { n: "01", title: "La Cagliata", body: "Tutto inizia dal miglior latte fresco e da una tecnica tramandata nel tempo. La cagliata riposa nel siero caldo finché diventa morbida ed elastica — il momento di quiete prima che il mestiere cominci." },
      { n: "02", title: "La Filatura", body: "A mano, la cagliata viene tirata e ripiegata ancora e ancora — pasta filata. Al tavolo, gli ospiti vedono i fili opachi farsi lucidi mentre una sfera calda di mozzarella prende forma davanti ai loro occhi." },
      { n: "03", title: "La Tavola", body: "Strappata ancora calda, condita con olio a crudo e sale marino, presentata con sobria cerimonia. Un primo assaggio che nessuna cucina può replicare — l'essenza del mestiere, servita." },
    ],
    tableKicker: "03 · La Tavola",
    tableH3: <>Servita calda, composta con <em className="italic">intenzione.</em></>,
    tableAlts: ["Burrata e pomodori antichi", "Caprese, a strati", "Burrata, aperta", "Mozzarella e prosciutto", "Stracciatella ed erbe", "Calda, su peperoni grigliati"],
  },
  ru: {
    kicker: "Ремесло · Pasta Filata",
    h2: <>Старинное ремесло, исполненное с <em className="italic">редкой точностью.</em></>,
    chapters: [
      { n: "01", title: "Сгусток", body: "Всё начинается с лучшего свежего молока и техники, передаваемой веками. Сгусток отдыхает в тёплой сыворотке, пока не станет мягким и эластичным." },
      { n: "02", title: "Вытягивание", body: "Вручную сгусток тянут и складывают снова и снова — pasta filata. Гости видят, как матовые нити становятся глянцевыми, и тёплая сфера моцареллы рождается у них на глазах." },
      { n: "03", title: "Стол", body: "Разорвана ещё тёплой, приправлена маслом первого отжима и морской солью, подана со сдержанной церемонией. Первый вкус, который не повторит ни одна кухня." },
    ],
    tableKicker: "03 · Стол",
    tableH3: <>Подано тёплым, составлено с <em className="italic">замыслом.</em></>,
    tableAlts: ["Буррата и старинные томаты", "Капрезе, слоями", "Буррата, раскрытая", "Моцарелла и прошутто", "Страчателла и травы", "Тёплая, на печёном перце"],
  },
  es: {
    kicker: "El Oficio · Pasta Filata",
    h2: <>Un oficio milenario, ejecutado con <em className="italic">rara precisión.</em></>,
    chapters: [
      { n: "01", title: "La Cuajada", body: "Todo empieza con la mejor leche fresca y una técnica transmitida en el tiempo. La cuajada reposa en el suero tibio hasta volverse suave y elástica — el momento de calma antes de que empiece el oficio." },
      { n: "02", title: "El Hilado", body: "A mano, la cuajada se estira y se pliega una y otra vez — pasta filata. En la mesa, los invitados ven las hebras opacas volverse brillantes mientras una esfera tibia de mozzarella toma forma ante sus ojos." },
      { n: "03", title: "La Mesa", body: "Desgarrada aún tibia, aliñada con aceite de oliva en crudo y sal marina, presentada con sobria ceremonia. Un primer bocado que ninguna cocina puede replicar — la esencia del oficio, servida." },
    ],
    tableKicker: "03 · La Mesa",
    tableH3: <>Servida tibia, compuesta con <em className="italic">intención.</em></>,
    tableAlts: ["Burrata y tomates antiguos", "Caprese, en capas", "Burrata, abierta", "Mozzarella y prosciutto", "Stracciatella y hierbas", "Tibia, sobre pimientos asados"],
  },
};

const Chapter = ({ c, img, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const flip = index % 2 === 1;

  return (
    <div ref={ref} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
      <div className={`md:col-span-6 ${flip ? "md:order-2" : ""}`}>
        <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-[0_20px_40px_-15px_rgba(28,25,23,0.15)]">
          <motion.img src={img} alt={c.title} style={{ y }} className="absolute inset-0 w-full h-[116%] object-cover" />
        </div>
      </div>
      <div className={`md:col-span-6 ${flip ? "md:order-1 md:pr-10" : "md:pl-10"}`}>
        <FadeUp>
          <span className="font-serif italic text-7xl md:text-8xl text-terracotta/90 leading-none block mb-6">{c.n}</span>
          <h3 className="font-serif text-4xl md:text-5xl text-ink mb-5">{c.title}</h3>
          <p className="font-sans text-base md:text-lg text-ink-soft max-w-md leading-relaxed">{c.body}</p>
        </FadeUp>
      </div>
    </div>
  );
};

export const Manifesto = () => {
  const { lang } = useLang();
  const t = copy[lang];
  return (
    <section id="craft" className="py-24 md:py-40 bg-gradient-to-b from-[#efe4d2] to-bg" data-testid="manifesto-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-16 md:mb-28 max-w-2xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-ink-soft mb-5">{t.kicker}</p>
          <h2 className="font-serif text-4xl md:text-6xl text-ink leading-[1.05]">{t.h2}</h2>
        </FadeUp>
        <div className="space-y-24 md:space-y-40">
          {t.chapters.map((c, i) => (
            <Chapter key={c.n} c={c} img={imgs[i]} index={i} />
          ))}
        </div>

        <FadeUp className="mt-16 md:mt-24" data-testid="table-gallery">
          <div className="bg-wine text-bg rounded-sm p-6 md:p-12">
            <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-3">{t.tableKicker}</p>
            <h3 className="font-serif text-3xl md:text-5xl leading-[1.02] mb-8 md:mb-10 max-w-2xl">{t.tableH3}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {tableSrcs.map((src, i) => (
                <figure key={i} className="group">
                  <div className="relative overflow-hidden rounded-sm aspect-[3/2]">
                    <img
                      src={src}
                      alt={t.tableAlts[i]}
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3">
                    <span className="font-serif italic text-bg/75 text-base md:text-lg">{t.tableAlts[i]}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
